import React, { useState } from "react";
import { Settlement, Merchant } from "@/shared/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Badge } from "@/shared/components/ui/badge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  MoreHorizontal,
  FileText,
  Download,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Eye,
  Send,
  DollarSign,
} from "lucide-react";
import { format } from "date-fns";

const C = {
  bg: "#060C18",
  card: "#0E1C35",
  cardHigh: "#122040",
  cardHover: "#162B4D",
  border: "rgba(255,255,255,0.07)",
  borderHover: "rgba(34,211,238,0.35)",
  cyan: "#22D3EE",
  cyanSoft: "rgba(34,211,238,0.08)",
  blue: "#3B82F6",
  blueSoft: "rgba(59,130,246,0.08)",
  purple: "#A78BFA",
  purpleSoft: "rgba(167,139,250,0.08)",
  emerald: "#10B981",
  emeraldSoft: "rgba(16,185,129,0.08)",
  amber: "#F59E0B",
  amberSoft: "rgba(245,158,11,0.08)",
  rose: "#FB7185",
  t1: "#EFF6FF",
  t2: "#93B4D4",
  t3: "#4D6E90",
  t4: "#2A4060",
};

const DARK_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

.settlements-dark, .settlements-dark * {
  font-family: 'Outfit', system-ui, sans-serif !important;
  box-sizing: border-box;
}

/* ── Search & inputs ── */
.settlements-dark .s-search-wrap {
  position: relative;
  flex: 1 1 250px;
  min-width: 250px;
  display: flex;
  align-items: center;
}

.settlements-dark .s-search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #4D6E90;
  pointer-events: none;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  transition: color .2s;
}

.settlements-dark .s-search-icon svg {
  display: block !important;
  color: inherit !important;
  width: 15px !important;
  height: 15px !important;
  flex-shrink: 0;
}

.settlements-dark .s-search-input {
  width: 100%;
  height: 42px;
  padding: 0 14px 0 40px !important;
  background: #0E1C35 !important;
  border: 1px solid rgba(255,255,255,0.09) !important;
  color: #EFF6FF !important;
  border-radius: 10px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all .2s cubic-bezier(0.4,0,0.2,1) !important;
  outline: none !important;
}

.settlements-dark .s-search-input::placeholder {
  color: #4D6E90 !important;
}

.settlements-dark .s-search-input:hover {
  border-color: rgba(34,211,238,0.35) !important;
  background: #122040 !important;
}

.settlements-dark .s-search-input:hover ~ .s-search-icon,
.settlements-dark .s-search-wrap:hover .s-search-icon {
  color: #22D3EE !important;
}

.settlements-dark .s-search-input:focus {
  border-color: rgba(34,211,238,0.55) !important;
  box-shadow: 0 0 0 3px rgba(34,211,238,0.12) !important;
  background: #122040 !important;
}

.settlements-dark .s-search-input:focus ~ .s-search-icon,
.settlements-dark .s-search-wrap:focus-within .s-search-icon {
  color: #22D3EE !important;
}

/* Make icon svg inherit color */
.settlements-dark .s-search-icon svg {
  display: block;
  color: inherit !important;
}

/* Dialog inputs */
.settlements-dark input[type="text"],
.settlements-dark input[type="number"],
.settlements-dark input[type="date"],
.settlements-dark input:not([type="checkbox"]):not([type="radio"]):not(.s-search-input) {
  background: #0E1C35 !important;
  border: 1px solid rgba(255,255,255,0.09) !important;
  color: #EFF6FF !important;
  border-radius: 10px !important;
  height: 40px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all .2s !important;
  padding: 0 14px !important;
  width: 100% !important;
}

.settlements-dark input:not(.s-search-input):hover {
  border-color: rgba(34,211,238,0.35) !important;
  background: #122040 !important;
}

.settlements-dark input:not(.s-search-input):focus {
  outline: none !important;
  border-color: rgba(34,211,238,0.55) !important;
  box-shadow: 0 0 0 3px rgba(34,211,238,0.12) !important;
  background: #122040 !important;
}

/* Select */
.settlements-dark [data-radix-select-trigger],
.settlements-dark [role="combobox"] {
  background: #0E1C35 !important;
  border: 1px solid rgba(255,255,255,0.09) !important;
  color: #EFF6FF !important;
  border-radius: 10px !important;
  height: 42px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all .2s !important;
}

.settlements-dark [data-radix-select-trigger]:hover,
.settlements-dark [role="combobox"]:hover {
  border-color: rgba(34,211,238,0.35) !important;
  color: #22D3EE !important;
  background: #122040 !important;
}

.settlements-dark [data-radix-select-content] {
  background: #0E1C35 !important;
  border: 1px solid rgba(34,211,238,0.2) !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
}

.settlements-dark [data-radix-select-item] {
  color: #93B4D4 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
}

.settlements-dark [data-radix-select-item]:hover,
.settlements-dark [data-radix-select-item][data-highlighted] {
  background: rgba(34,211,238,0.08) !important;
  color: #22D3EE !important;
}

/* Labels */
.settlements-dark label,
.settlements-dark [data-label] {
  color: #93B4D4 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
}

/* Dialog */
.settlements-dark [role="dialog"] {
  background: #0E1C35 !important;
  border: 1px solid rgba(34,211,238,0.15) !important;
  border-radius: 18px !important;
  box-shadow: 0 24px 64px rgba(0,0,0,0.7) !important;
  color: #EFF6FF !important;
}

.settlements-dark [role="dialog"] h2 { color: #EFF6FF !important; }

/* Dropdown */
.settlements-dark [data-radix-dropdown-menu-content] {
  background: #0E1C35 !important;
  border: 1px solid rgba(34,211,238,0.15) !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
}

.settlements-dark [data-radix-dropdown-menu-item] {
  color: #93B4D4 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all .15s !important;
}

.settlements-dark [data-radix-dropdown-menu-item]:hover,
.settlements-dark [data-radix-dropdown-menu-item][data-highlighted] {
  background: rgba(34,211,238,0.08) !important;
  color: #22D3EE !important;
}

/* Buttons */
.settlements-dark button[data-cancel-btn] {
  background: transparent !important;
  border: 1px solid rgba(255,255,255,0.09) !important;
  color: #93B4D4 !important;
  border-radius: 10px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  transition: all .2s !important;
}

.settlements-dark button[data-cancel-btn]:hover {
  border-color: rgba(34,211,238,0.35) !important;
  color: #22D3EE !important;
}
/* Container relative for absolute positioning */
.settlements-dark .s-search-wrap {
  position: relative;
  width: 250px; /* adjust as needed */
  display: block; /* remove flex interfering with absolute icon */
}

/* Input */
.settlements-dark .s-search-input {
  width: 100%;
  height: 42px;
  padding-left: 25px; /* space for icon */
  padding-right: 12px;
  background: #0E1C35;
  border: 1px solid rgba(255,255,255,0.09);
  color: #EFF6FF;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  box-sizing: border-box;
  outline: none;
}

/* Icon */
.settlements-dark .s-search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #4D6E90;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

@keyframes spin { to { transform: rotate(360deg); } }

::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: rgba(14,28,53,0.3); border-radius: 10px; }
::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.2); border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: rgba(34,211,238,0.35); }
`;

// Backend uses UPPERCASE enum values: PENDING, PROCESSING, COMPLETED, FAILED
const statusConfig = {
  PENDING: { icon: Clock, color: C.amber, bg: C.amberSoft, label: "PENDING" },
  PROCESSING: { icon: Loader2, color: C.blue, bg: C.blueSoft, label: "PROCESSING", spin: true },
  COMPLETED: { icon: CheckCircle2, color: C.emerald, bg: C.emeraldSoft, label: "COMPLETED" },
  FAILED: { icon: XCircle, color: C.rose, bg: "rgba(251,113,133,0.08)", label: "FAILED" },
};

// ── Export helper ──────────────────────────────────────────────────────────
function exportSettlementCSV(settlement) {
  const rows = [
    ["Field", "Value"],
    ["Settlement ID", settlement.id ?? ""],
    ["Merchant Name", settlement.merchant?.name ?? ""],
    ["Merchant Code", settlement.merchant_code ?? ""],
    ["Status", settlement.status ?? ""],
    ["Currency", settlement.currency ?? "THB"],
    ["Period Start", settlement.period_start ? format(new Date(settlement.period_start), "yyyy-MM-dd") : ""],
    ["Period End", settlement.period_end ? format(new Date(settlement.period_end), "yyyy-MM-dd") : ""],
    ["Total Deposits", settlement.total_deposits ?? 0],
    ["Total Withdrawals", settlement.total_withdrawals ?? 0],
    ["Total Fees", settlement.total_fees ?? 0],
    ["Net Amount", settlement.net_amount ?? 0],
    ["Notes", settlement.notes ?? ""],
    ["Created At", settlement.createdAt ? format(new Date(settlement.createdAt), "yyyy-MM-dd HH:mm:ss") : ""],
    ["Updated At", settlement.updatedAt ? format(new Date(settlement.updatedAt), "yyyy-MM-dd HH:mm:ss") : ""],
  ];
  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `settlement_${settlement.id?.slice(0, 8) ?? "report"}_${format(new Date(), "yyyyMMdd")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Settlements() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const { t } = useLanguage();

  React.useEffect(() => {
    const id = "settlements-dark-css";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = DARK_CSS;
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);

  const queryClient = useQueryClient();

  const { data: settlements = [], isLoading } = useQuery({
    queryKey: ["settlements"],
    queryFn: () => Settlement.list("-created_date", 200),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => Merchant.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => Settlement.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["settlements"]);
      setCreateOpen(false);
      toast.success("Settlement created", {
        description: "The new settlement has been saved successfully.",
      });
    },
    onError: (err) =>
      toast.error("Failed to create settlement", {
        description: err?.message || "An unexpected error occurred. Please try again.",
      }),
  });

  const processMutation = useMutation({
    mutationFn: (id) => Settlement.process(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["settlements"]);
      toast.success("Settlement moved to PROCESSING");
    },
    onError: (err) => toast.error(err?.message || "Failed to process settlement"),
  });

  const completeMutation = useMutation({
    mutationFn: (id) => Settlement.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["settlements"]);
      toast.success("Settlement marked as COMPLETED");
    },
    onError: (err) => toast.error(err?.message || "Failed to complete settlement"),
  });

  const filteredSettlements = settlements.filter((s) => {
    if (search) {
      const sl = search.toLowerCase();
      const idMatch = s.id?.toLowerCase().includes(sl);
      const codeMatch = s.merchant_code?.toLowerCase().includes(sl);
      const nameMatch = s.merchant?.name?.toLowerCase().includes(sl);
      if (!idMatch && !codeMatch && !nameMatch) return false;
    }
    // statusFilter uses UPPERCASE to match backend enum
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    return true;
  });

  const handleProcess = (settlement) => processMutation.mutate(settlement.id);
  const handleComplete = (settlement) => completeMutation.mutate(settlement.id);

  return (
    <div className="settlements-dark" style={{ minHeight: "100vh", background: C.bg }}>
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(34,211,238,0.015) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,0.015) 1px,transparent 1px)", backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse 80% 80% at 50% 30%,black 20%,transparent 100%)" }} />
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse 1000px 600px at 5% 5%,rgba(34,211,238,0.04) 0%,transparent 70%),radial-gradient(ellipse 800px 500px at 95% 85%,rgba(167,139,250,0.04) 0%,transparent 65%)" }} />

      <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <div style={{ width: 60, height: 60, borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "linear-gradient(135deg,rgba(34,211,238,0.1) 0%,rgba(167,139,250,0.1) 100%)", border: "1px solid rgba(34,211,238,0.22)", boxShadow: "0 8px 24px rgba(34,211,238,0.1)" }}>
              <DollarSign size={26} style={{ color: C.cyan }} />
            </div>
            <div>
              <h1 style={{ fontSize: 30, fontWeight: 800, color: C.t1, margin: 0, letterSpacing: "-0.6px" }}>{t("settlementTitle")}</h1>
              <p style={{ fontSize: 13, color: C.t3, margin: "6px 0 0", fontWeight: 500 }}>{t("merchantPayoutManagement")}</p>
            </div>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "0 20px", height: 44, borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "1px solid rgba(34,211,238,0.3)", background: "linear-gradient(135deg,rgba(34,211,238,0.12) 0%,rgba(59,130,246,0.12) 100%)", color: C.cyan, transition: "all .2s", boxShadow: "0 4px 16px rgba(34,211,238,0.15)" }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(34,211,238,0.25)"; e.currentTarget.style.borderColor = "rgba(34,211,238,0.5)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(34,211,238,0.15)"; e.currentTarget.style.borderColor = "rgba(34,211,238,0.3)"; }}
          >
            <Plus size={16} />
            {t("createSettlement")}
          </button>
        </div>

        {/* ── Filters (FIXED) ── */}
        <div style={{ background: `linear-gradient(155deg,${C.cardHigh} 0%,${C.card} 100%)`, border: `1px solid ${C.border}`, borderRadius: 16, padding: "18px 20px", boxShadow: "0 4px 24px rgba(0,0,0,0.2)", marginBottom: 24, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,rgba(34,211,238,0.15),transparent)" }} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            {/* Search — icon wrapped in span for correct absolute positioning */}
            <div className="s-search-wrap">
              <Input
                placeholder={t("searchSettlements")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="s-search-input"
                style={{ paddingLeft: 30 }}
              />
              <span className="s-search-icon">
                <Search size={15} />
              </span>
            </div>
            {/* Status Select */}
            <div style={{ flex: "0 0 auto" }}>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger style={{ width: 160 }}>
                  <SelectValue placeholder={t("status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatus")}</SelectItem>
                  <SelectItem value="PENDING">{t("pending")}</SelectItem>
                  <SelectItem value="PROCESSING">{t("processing")}</SelectItem>
                  <SelectItem value="COMPLETED">{t("completed")}</SelectItem>
                  <SelectItem value="FAILED">{t("failed")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{ background: `linear-gradient(155deg,${C.cardHigh} 0%,${C.card} 100%)`, border: `1px solid ${C.border}`, borderRadius: 18, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.2)", position: "relative" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,rgba(34,211,238,0.15),transparent)" }} />
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {[t("settlementId"), t("merchant"), t("period"), t("deposits"), t("withdrawals"), t("netAmount"), t("status"), ""].map((h, i) => (
                    <th key={i} style={{ padding: "18px 20px", textAlign: i >= 3 && i <= 5 ? "right" : i === 7 ? "center" : "left", fontSize: 11, fontWeight: 700, color: C.t3, textTransform: "uppercase", letterSpacing: "0.1em", background: "rgba(255,255,255,0.02)", width: i === 7 ? 60 : "auto" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 60, fontSize: 14, color: C.t3, fontWeight: 500 }}>
                    <Loader2 size={24} style={{ color: C.cyan, animation: "spin 1s linear infinite", margin: "0 auto 8px", display: "block" }} />
                    {t("loading")}
                  </td></tr>
                ) : filteredSettlements.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 60, fontSize: 14, color: C.t3, fontWeight: 500 }}>{t("noSettlements")}</td></tr>
                ) : filteredSettlements.map((settlement) => {
                  const status = statusConfig[settlement.status] || statusConfig.PENDING;
                  const StatusIcon = status.icon;
                  return (
                    <tr key={settlement.id} style={{ borderBottom: `1px solid ${C.border}`, transition: "background .2s" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(34,211,238,0.03)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={{ padding: "20px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: 40, height: 40, borderRadius: 11, background: C.purpleSoft, border: "1px solid rgba(167,139,250,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FileText size={18} style={{ color: C.purple }} />
                          </div>
                          <div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: C.t1, fontFamily: "monospace", display: "block" }}>{settlement.id?.slice(0, 12)}…</span>
                            <span style={{ fontSize: 11, color: C.t3, fontWeight: 500 }}>{settlement.createdAt ? format(new Date(settlement.createdAt), "MMM d, yyyy") : ""}</span>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "20px" }}>
                        <div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.t1, display: "block" }}>{settlement.merchant?.name || "—"}</span>
                          <span style={{ fontSize: 11, color: C.t3, fontWeight: 500 }}>{settlement.merchant_code}</span>
                        </div>
                      </td>
                      <td style={{ padding: "20px" }}>
                        <div style={{ fontSize: 12 }}>
                          <div style={{ color: C.t1, fontWeight: 600 }}>{settlement.period_start ? format(new Date(settlement.period_start), "MMM d") : "N/A"}</div>
                          <div style={{ color: C.t3, marginTop: 2 }}>to {settlement.period_end ? format(new Date(settlement.period_end), "MMM d, yyyy") : "N/A"}</div>
                        </div>
                      </td>
                      <td style={{ padding: "20px", textAlign: "right" }}><span style={{ fontSize: 14, fontWeight: 700, color: C.blue }}>+฿{settlement.total_deposits?.toLocaleString() || 0}</span></td>
                      <td style={{ padding: "20px", textAlign: "right" }}><span style={{ fontSize: 14, fontWeight: 700, color: C.emerald }}>-฿{settlement.total_withdrawals?.toLocaleString() || 0}</span></td>
                      <td style={{ padding: "20px", textAlign: "right" }}><span style={{ fontSize: 15, fontWeight: 800, color: C.cyan }}>฿{settlement.net_amount?.toLocaleString() || 0}</span></td>
                      <td style={{ padding: "20px" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: status.bg, border: `1px solid ${status.color}30`, color: status.color }}>
                          <StatusIcon size={12} style={{ animation: status.spin ? "spin 1s linear infinite" : "none" }} />
                          {settlement.status}
                        </span>
                      </td>
                      <td style={{ padding: "20px", textAlign: "center" }}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, cursor: "pointer", background: C.card, border: `1px solid ${C.border}`, color: C.t2, transition: "all .2s" }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = C.cardHover; e.currentTarget.style.borderColor = C.borderHover; e.currentTarget.style.color = C.cyan; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = C.card; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t2; }}
                            >
                              <MoreHorizontal size={14} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setSelectedSettlement(settlement)}><Eye className="h-4 w-4 mr-2" />{t("viewDetails")}</DropdownMenuItem>
                            {settlement.status === "PENDING" && <DropdownMenuItem onClick={() => handleProcess(settlement)}><Send className="h-4 w-4 mr-2" />{t("process")}</DropdownMenuItem>}
                            {settlement.status === "PROCESSING" && <DropdownMenuItem onClick={() => handleComplete(settlement)}><CheckCircle2 className="h-4 w-4 mr-2" />{t("markCompleted")}</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => exportSettlementCSV(settlement)}><Download className="h-4 w-4 mr-2" />{t("exportReport")}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <CreateSettlementDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          merchants={merchants}
          isSubmitting={createMutation.isPending}
          onCreate={(data) => createMutation.mutate(data)}
        />

        <SettlementDetailDialog
          settlement={selectedSettlement}
          onClose={() => setSelectedSettlement(null)}
          onExport={() => { exportSettlementCSV(selectedSettlement); }}
        />
      </div>
    </div>
  );
}

// ── Settlement Detail Dialog ────────────────────────────────────────────────
function SettlementDetailDialog({ settlement, onClose, onExport }) {
  const { t } = useLanguage();
  if (!settlement) return null;
  const status = statusConfig[settlement.status] || statusConfig.PENDING;
  const StatusIcon = status.icon;

  const InfoRow = ({ label, value, valueColor }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: valueColor || C.t1 }}>{value}</span>
    </div>
  );

  return (
    <Dialog open={!!settlement} onOpenChange={onClose}>
      <DialogContent className="max-w-lg settlements-dark" style={{ maxHeight: "90vh", overflowY: "auto" }}>
        {/* Glow accent */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${status.color}60, transparent)`, borderRadius: "18px 18px 0 0" }} />

        <DialogHeader style={{ paddingBottom: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 46, height: 46, borderRadius: 13, background: C.purpleSoft, border: "1px solid rgba(167,139,250,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <FileText size={22} style={{ color: C.purple }} />
            </div>
            <div>
              <DialogTitle style={{ color: C.t1, fontSize: 17, fontWeight: 800, margin: 0, letterSpacing: "-0.3px" }}>
                Settlement Details
              </DialogTitle>
              <p style={{ margin: "3px 0 0", fontSize: 11, color: C.t3, fontWeight: 500, fontFamily: "monospace" }}>
                {settlement.id}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 0 }}>
          {/* Status badge */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.07em" }}>Status</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: status.bg, border: `1px solid ${status.color}30`, color: status.color }}>
              <StatusIcon size={12} style={{ animation: status.spin ? "spin 1s linear infinite" : "none" }} />
              {settlement.status}
            </span>
          </div>

          {/* Merchant */}
          <InfoRow label="Merchant" value={settlement.merchant?.name || "—"} valueColor={C.cyan} />
          <InfoRow label="Merchant Code" value={settlement.merchant_code || "—"} />

          {/* Period */}
          <InfoRow
            label="Period"
            value={
              `${settlement.period_start ? format(new Date(settlement.period_start), "MMM d, yyyy") : "N/A"}  →  ${settlement.period_end ? format(new Date(settlement.period_end), "MMM d, yyyy") : "N/A"}`
            }
          />

          {/* Financials */}
          <div style={{ margin: "14px 0 6px", fontSize: 11, fontWeight: 700, color: C.t3, textTransform: "uppercase", letterSpacing: "0.09em" }}>Financial Summary</div>
          <div style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}`, borderRadius: 12, padding: "4px 14px" }}>
            <InfoRow label="Total Deposits" value={`+฿${(settlement.total_deposits ?? 0).toLocaleString()}`} valueColor={C.blue} />
            <InfoRow label="Total Withdrawals" value={`-฿${(settlement.total_withdrawals ?? 0).toLocaleString()}`} valueColor={C.emerald} />
            <InfoRow label="Total Fees" value={`฿${(settlement.total_fees ?? 0).toLocaleString()}`} valueColor={C.amber} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0 6px" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.t2 }}>Net Amount</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: C.cyan }}>฿{(settlement.net_amount ?? 0).toLocaleString()}</span>
            </div>
          </div>

          {/* Notes */}
          {settlement.notes && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: 8 }}>Notes</div>
              <div style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", fontSize: 13, color: C.t2, lineHeight: 1.6 }}>
                {settlement.notes}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { label: "Created", val: settlement.createdAt },
              { label: "Last Updated", val: settlement.updatedAt },
            ].map(({ label, val }) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.t2 }}>{val ? format(new Date(val), "MMM d, yyyy HH:mm") : "—"}</div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter style={{ marginTop: 20, gap: 10 }}>
          <button
            onClick={onClose}
            data-cancel-btn
            style={{ padding: "0 18px", height: 40, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", background: "transparent", border: `1px solid ${C.border}`, color: C.t2, transition: "all .2s" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.borderHover; e.currentTarget.style.color = C.cyan; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t2; }}
          >
            Close
          </button>
          <button
            onClick={onExport}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "0 20px", height: 40, borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", background: "linear-gradient(135deg,rgba(34,211,238,0.15),rgba(59,130,246,0.15))", border: "1px solid rgba(34,211,238,0.3)", color: C.cyan, transition: "all .2s" }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(34,211,238,0.2)"; e.currentTarget.style.borderColor = "rgba(34,211,238,0.5)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "rgba(34,211,238,0.3)"; }}
          >
            <Download size={14} /> Export CSV
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateSettlementDialog({ open, onClose, merchants, onCreate, isSubmitting = false }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ merchant_id: "", period_start: "", period_end: "", total_deposits: 0, total_withdrawals: 0, total_fees: 0, net_amount: 0, notes: "" });
  const [saving, setSaving] = useState(false);
  const busy = saving || isSubmitting;

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  React.useEffect(() => {
    setFormData(prev => ({ ...prev, net_amount: (prev.total_deposits || 0) - (prev.total_withdrawals || 0) - (prev.total_fees || 0) }));
  }, [formData.total_deposits, formData.total_withdrawals, formData.total_fees]);

  const handleSubmit = async () => {
    if (!formData.merchant_id || !formData.period_start || !formData.period_end) return;
    setSaving(true);
    try {
      // Only send fields that match CreateSettlementInput on the backend
      await onCreate({
        merchant_id: formData.merchant_id,
        period_start: formData.period_start,
        period_end: formData.period_end,
        total_deposits: parseFloat(formData.total_deposits) || 0,
        total_withdrawals: parseFloat(formData.total_withdrawals) || 0,
        total_fees: parseFloat(formData.total_fees) || 0,
        currency: "THB",
        notes: formData.notes || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md settlements-dark">
        <DialogHeader>
          <DialogTitle style={{ color: C.t1, fontSize: 18, fontWeight: 700 }}>{t("createSettlement")}</DialogTitle>
        </DialogHeader>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: C.t2, textTransform: "uppercase", letterSpacing: "0.06em" }}>{t("merchant")} *</label>
            <Select value={formData.merchant_id} onValueChange={(v) => handleChange("merchant_id", v)}>
              <SelectTrigger><SelectValue placeholder={t("selectMerchant")} /></SelectTrigger>
              <SelectContent>{merchants.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.code})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[{ key: "period_start", label: t("periodStart") }, { key: "period_end", label: t("periodEnd") }].map(({ key, label }) => (
              <div key={key} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: C.t2, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label} *</label>
                <input type="date" value={formData[key]} onChange={(e) => handleChange(key, e.target.value)} />
              </div>
            ))}
          </div>
          {[["total_deposits", t("totalDeposits")], ["total_withdrawals", t("totalWithdrawals")], ["total_fees", t("totalFees")]].map(([key, label]) => (
            <div key={key} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: C.t2, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label} (THB)</label>
              <input type="number" value={formData[key]} onChange={(e) => handleChange(key, e.target.value)} />
            </div>
          ))}
          <div style={{ padding: 16, borderRadius: 12, background: C.cyanSoft, border: "1px solid rgba(34,211,238,0.25)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.cyan }}>{t("netAmount")}:</span>
            <span style={{ fontSize: 20, fontWeight: 800, color: C.cyan }}>฿{formData.net_amount.toLocaleString()}</span>
          </div>
        </div>
        <DialogFooter style={{ marginTop: 20, gap: 10 }}>
          {/* Cancel – disabled while saving */}
          <button
            onClick={onClose}
            disabled={busy}
            data-cancel-btn
            style={{
              padding: "0 18px", height: 40, borderRadius: 10, fontSize: 13, fontWeight: 600,
              cursor: busy ? "not-allowed" : "pointer",
              background: "transparent",
              border: `1px solid ${C.border}`,
              color: C.t2,
              transition: "all .2s",
              opacity: busy ? 0.4 : 1,
            }}
            onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.borderColor = C.borderHover; e.currentTarget.style.color = C.cyan; } }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t2; }}
          >{t("cancel")}</button>

          {/* Submit – spinner during loading */}
          <button
            onClick={handleSubmit}
            disabled={busy || !formData.merchant_id || !formData.period_start || !formData.period_end}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "0 20px", height: 40, borderRadius: 10, fontSize: 13, fontWeight: 700,
              cursor: (busy || !formData.merchant_id || !formData.period_start || !formData.period_end) ? "not-allowed" : "pointer",
              background: "linear-gradient(135deg,rgba(34,211,238,0.15),rgba(59,130,246,0.15))",
              border: "1px solid rgba(34,211,238,0.3)",
              color: C.cyan,
              transition: "all .2s",
              opacity: (busy || !formData.merchant_id || !formData.period_start || !formData.period_end) ? 0.55 : 1,
              pointerEvents: busy ? "none" : "auto",
            }}
            onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.boxShadow = "0 4px 16px rgba(34,211,238,0.2)"; e.currentTarget.style.borderColor = "rgba(34,211,238,0.5)"; } }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "rgba(34,211,238,0.3)"; }}
          >
            {busy ? (
              <>
                <Loader2 size={14} style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
                {t("creating")}…
              </>
            ) : (
              t("createPayout")
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}